import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import Avatar from 'components/@extended/Avatar';
import IconButton from 'components/@extended/IconButton';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';

import { useAuth } from '../../../../../contexts/AuthContext';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signout, user } = useAuth();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const isActive = location.pathname === '/account/settings';

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setLogoutLoading(true);
    try {
      await signout();
      navigate('/login');
    } catch (err) {
      // If signout fails we still clear local UI and navigate to login to be safe.
      // Optionally surface an error to the user here (snackbar/toast).
      console.error('Signout failed', err);
      navigate('/login');
    } finally {
      setLogoutLoading(false);
      setLogoutDialogOpen(false);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleAccountSettings = () => {
    navigate('/account/settings');
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', p: 0.5 }}>
        <Grid>
          <Tooltip title="Account Settings">
            <Button
              onClick={handleAccountSettings}
              variant={isActive ? 'outlined' : 'text'}
              color="primary"
              startIcon={
                <Avatar alt="profile user" size="sm" sx={{ fontWeight: 'bold' }}>
                  {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              }
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '0.875rem'
                }}
              >
                {user?.username || 'User'}
              </Typography>
            </Button>
          </Tooltip>
        </Grid>

        <Grid>
          <Tooltip title="Logout">
            <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogoutClick}>
              <LogoutOutlined />
            </IconButton>
          </Tooltip>
        </Grid>
      </Stack>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={logoutLoading ? undefined : handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out? You will need to sign in again to access your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary" disabled={logoutLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            color="error"
            variant="contained"
            autoFocus
            disabled={logoutLoading}
            startIcon={logoutLoading ? <CircularProgress color="inherit" size={18} /> : null}
          >
            {logoutLoading ? 'Logging out...' : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
